<template>
  <div>
    <v-btn @click="login" :loading="loading">
      <v-icon left>{{ icons.mdiApple }}</v-icon>{{ $t('auth.SignInWithAppleButton') }}
    </v-btn>
    <login-failed-dialog v-if="showLoginFailedDialog" :show="showLoginFailedDialog" @closeDialog="showLoginFailedDialog = false"/>

  </div>
</template>

<script>
  import _ from 'lodash'
  import {BASE_API_URL} from '@/store'
  import {mapGetters, mapActions, mapState} from 'vuex'
  import log from 'loglevel'
  import LoginFailedDialog from '@/components/auth/LoginFailedDialog'
  import {mdiApple} from '@mdi/js'

  export default {
    name: "AppleLogin",
    components: {
      LoginFailedDialog
    },
    data() {
      return {
        dialog: false,
        appleClientUrl: 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
        loading: false,
        showLoginFailedDialog: false,
        icons: {
          mdiApple
        }
      }
    },
    computed: {
      ...mapState(['client_id']),
      ...mapGetters([
        'extensionSettings',
        'isAuthenticated',
        'isOpenIdProvider'
      ])
    },
    mounted() {
      // Call the loadAppleApi method when the component is mounted
      this.loadAppleApi()
    },
    methods: {
      ...mapActions([
        'tosScreen',
        'userAuthenticate'
      ]),
      // Handle the Apple Sign-In credential response
      async handleCredentialResponse(authResponse) {
        // The ID token
        const idToken = _.get(authResponse, 'authorization.id_token')

        // send the ID token to server for verification
        if (idToken) {
          try {
            // Send the token to server for verification
            let response = await this.axios.post(
              `${BASE_API_URL}/api/v1/users/apple-login/?client_id=${this.client_id}`,
              {
                access_token: idToken,
                locale: this.$i18n.locale
              }
            )
            this.tosScreen({show: false})
            if (response.data) {
              await this.userAuthenticate({access_token: _.get(response.data, 'access_token', '')})
              if (this.isAuthenticated) {
                this.tosScreen({show: false})
                if (this.isOpenIdProvider) {
                  this.$router.push({name: 'openid'})
                } else {
                  this.$router.push({name: 'home'})
                }
              }
            }
          } catch (error) {
            this.handleErrorCredentialResponse(error)
          } finally {
            this.loading = false
          }
        }
      },
      // Handle the error response
      handleErrorCredentialResponse(error) {
        log.error('error: ', error)
        this.tosScreen({show: false})
        this.showLoginFailedDialog = true
        this.loading = false
      },
      // Initialize Apple Sign-In once the script has been loaded
      initAppleSignIn() {
        try {
          window.AppleID.auth.init({
            clientId: _.get(this.extensionSettings, "auth.apple_login.web_service_id"), // Service ID
            scope: "name email", // Requested scopes
            redirectURI: _.get(this.extensionSettings, "webview.base_url"), // redirect URI
            usePopup: true
          })
        } catch (err) {
          log.error(err)
        }
      },
      // Load the Apple Sign-In script
      async loadAppleApi() {
        try {
          const el = document.getElementById('apple_script_id')
          if (!el) {
            let applePlatformScript = document.createElement('script')
            applePlatformScript.setAttribute('src', this.appleClientUrl)
            applePlatformScript.setAttribute('async', true)
            applePlatformScript.setAttribute('defer', 'defer')
            applePlatformScript.setAttribute('id', 'apple_script_id')
            applePlatformScript.onload = this.initAppleSignIn
            // Append the script element to the document's <head> section
            document.head.appendChild(applePlatformScript)
          }
        } catch (error) {
          log.error('error loading Apple API', error)
        }
      },
      login() {
        this.tosScreen({show: true, acceptAction: this.performLogin, rejectAction: this.rejectTOS})
      },
      async performLogin() {
        this.loading = true
        try {
          await this.signIn()
        } catch (error) {
          this.handleErrorCredentialResponse(error)
        } finally {
          this.loading = false
        }
      },
      rejectTOS() {
        this.tosScreen({show: false})
      },
      async signIn() {
        try {
          const response = await window.AppleID.auth.signIn()
          if (response) {
            this.handleCredentialResponse(response)
          }
        }
        catch(e) {
          this.handleErrorCredentialResponse(e)
        }
      }
    }
  }
</script>

<style scoped>
  .v-btn {
    color: #fff;
    background-color: #000 !important;
    text-transform: none;
    letter-spacing: 0;
    border: 1px solid #c2c2c2;
  }
  .v-btn svg {
    margin-left: -4px;
    margin-right: 8px;
  }
</style>
